import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    MoreHorizontal, Trash2, EyeOff, Lock, ChevronDown, ChevronUp, Pin, PinOff, Mail, Paperclip,
    ChevronLeft, ChevronRight, Maximize2, X, Download, Presentation
} from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import TituloConMarcas from './TituloConMarcas';

const FEED_REACTION_EMOJIS = ['👍', '❤️', '👏', '🎉', '🙏', '😮'];

// Componente de una sola pregunta dentro del card
const PreguntaPoll = ({ pregunta, postId, postIsExpired, postIsAnonima, canSeeResults, isAdminOrCreator, onVoted }) => {
    const hasVotedOrExpired = pregunta.user_has_voted || postIsExpired;
    const showResults = hasVotedOrExpired && canSeeResults;
    const showVotedButHidden = hasVotedOrExpired && !canSeeResults;
    const canSeeVoters = !postIsAnonima && isAdminOrCreator && hasVotedOrExpired;

    const [expandedVoters, setExpandedVoters] = useState(null); // opcion id

    const esTexto = pregunta.tipo === 'texto';
    const [textoRespuesta, setTextoRespuesta] = useState('');
    const [enviando, setEnviando] = useState(false);

    const handleEnviarTexto = async () => {
        const texto = textoRespuesta.trim();
        if (!texto || enviando) return;
        setEnviando(true);
        try {
            await axiosInstance.post(`/api/publicaciones/${postId}/voto`, {
                pregunta_id: pregunta.id,
                respuesta_texto: texto,
            });
            onVoted();
        } catch (error) {
            alert(error.response?.data?.error || 'Error al enviar la respuesta');
        } finally {
            setEnviando(false);
        }
    };

    const handleVote = async (opcionId) => {
        try {
            await axiosInstance.post(`/api/publicaciones/${postId}/voto`, {
                opcion_id: opcionId,
                pregunta_id: pregunta.id,
            });
            onVoted();
        } catch (error) {
            alert(error.response?.data?.error || 'Error al registrar voto');
        }
    };

    // Pregunta de respuesta abierta (texto libre)
    if (esTexto) {
        if (!hasVotedOrExpired) {
            return (
                <div className="space-y-2">
                    <textarea
                        value={textoRespuesta}
                        onChange={(e) => setTextoRespuesta(e.target.value.slice(0, 1000))}
                        placeholder="Escribe tu respuesta..."
                        rows={3}
                        className="w-full px-3 py-2.5 bg-white border border-blue-100/50 rounded-xl shadow-sm text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-sura-blue/30 focus:border-sura-blue resize-none placeholder:text-gray-400"
                    />
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] text-gray-400 tabular-nums">{textoRespuesta.length}/1000</span>
                        <button
                            onClick={handleEnviarTexto}
                            disabled={!textoRespuesta.trim() || enviando}
                            className="px-4 py-1.5 bg-sura-blue text-white text-sm font-semibold rounded-lg shadow-sm hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                        >
                            {enviando ? 'Enviando...' : 'Enviar respuesta'}
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div className="space-y-2">
                {pregunta.user_respuesta_texto && (
                    <div className="px-3 py-2.5 bg-sura-blue/5 border border-sura-blue/20 rounded-lg">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-sura-blue mb-1">Tu respuesta</p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{pregunta.user_respuesta_texto}</p>
                    </div>
                )}
                {showResults && (pregunta.respuestas || []).length > 0 && (
                    <div className="space-y-1.5">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mt-2">
                            {pregunta.respuestas.length} {pregunta.respuestas.length === 1 ? 'respuesta' : 'respuestas'}
                        </p>
                        {pregunta.respuestas.map((r, i) => (
                            <div key={i} className="px-3 py-2 bg-gray-50 border border-gray-100 rounded-lg">
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{r.texto}</p>
                                {r.usuario && <p className="text-[11px] text-gray-400 mt-0.5">{r.usuario.name}</p>}
                            </div>
                        ))}
                    </div>
                )}
                {showVotedButHidden && (
                    <div className="flex items-center justify-center gap-1.5 text-xs text-gray-400 mt-2">
                        <Lock size={11} />
                        <span>Respuestas visibles solo para el creador</span>
                    </div>
                )}
            </div>
        );
    }

    // Vista: ya votó pero resultados privados
    if (showVotedButHidden) {
        return (
            <div className="space-y-2">
                {pregunta.options.map((option) => {
                    const isSelected = pregunta.user_voted_option_id === option.id;
                    return (
                        <div key={option.id} className={`relative h-10 w-full rounded-lg overflow-hidden border shadow-sm flex items-center px-3 gap-2 ${isSelected ? 'bg-sura-blue/5 border-sura-blue/20' : 'bg-gray-50 border-gray-100'}`}>
                            {isSelected && (
                                <div className="w-4 h-4 rounded-full bg-sura-blue text-white flex items-center justify-center shadow-sm flex-shrink-0">
                                    <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            )}
                            <span className={`text-sm font-medium ${isSelected ? 'text-sura-blue' : 'text-gray-500'}`}>
                                {option.texto}
                            </span>
                        </div>
                    );
                })}
                <div className="flex items-center justify-center gap-1.5 text-xs text-gray-400 mt-2">
                    <Lock size={11} />
                    <span>Resultados visibles solo para el creador</span>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            {pregunta.options.map((option) => {
                const isSelected = pregunta.user_voted_option_id === option.id;
                const hasVoters = canSeeVoters && option.voters?.length > 0;

                return (
                    <div key={option.id}>
                        {showResults ? (
                            // Vista de resultados
                            <div className="relative">
                                <div className="relative h-10 w-full bg-white rounded-lg overflow-hidden border border-gray-100 shadow-sm">
                                    <div
                                        className={`absolute top-0 left-0 h-full transition-all duration-700 ease-out ${isSelected ? 'bg-sura-blue/10' : 'bg-gray-100/60'}`}
                                        style={{ width: `${option.percentage}%` }}
                                    />
                                    <div className="absolute inset-0 flex items-center justify-between px-3 z-10">
                                        <div className="flex items-center gap-2">
                                            {isSelected && (
                                                <div className="w-4 h-4 rounded-full bg-sura-blue text-white flex items-center justify-center shadow-sm flex-shrink-0">
                                                    <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </div>
                                            )}
                                            <span className={`text-sm font-medium ${isSelected ? 'text-sura-blue' : 'text-gray-700'}`}>
                                                {option.texto}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-bold text-gray-700">{Math.round(option.percentage)}%</span>
                                            {hasVoters && (
                                                <button
                                                    onClick={() => setExpandedVoters(expandedVoters === option.id ? null : option.id)}
                                                    className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-0.5"
                                                >
                                                    {option.voters.length}
                                                    {expandedVoters === option.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {/* Lista de votantes */}
                                {hasVoters && expandedVoters === option.id && (
                                    <div className="mt-1 ml-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100 animate-fadeIn">
                                        <div className="flex flex-wrap gap-1.5">
                                            {option.voters.map(v => (
                                                <span key={v.id} className="text-xs bg-white border border-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                                                    {v.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            // Vista de votación
                            <button
                                onClick={() => handleVote(option.id)}
                                className="w-full text-left px-4 py-2.5 bg-white border border-blue-100/50 rounded-xl shadow-sm hover:shadow-md hover:border-sura-blue hover:-translate-y-0.5 transition-all duration-200 group flex items-center gap-3"
                            >
                                <div className="w-4 h-4 rounded-full border-2 border-gray-300 group-hover:border-sura-blue flex items-center justify-center transition-colors flex-shrink-0">
                                    <div className="w-2 h-2 rounded-full bg-sura-blue opacity-0 group-hover:opacity-100 transition-opacity transform scale-0 group-hover:scale-100" />
                                </div>
                                <span className="text-sm font-medium text-gray-700 group-hover:text-sura-blue transition-colors">
                                    {option.texto}
                                </span>
                            </button>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const ReactionBar = ({ itemType, itemId, reactions = {}, allowReactions, userId, onReactionsChange }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const reactionEntries = Object.entries(reactions).sort(([, usersA], [, usersB]) => usersB.length - usersA.length);

    if (!allowReactions) {
        return null;
    }

    const handleToggleReaction = async (emoji) => {
        if (isSubmitting) return;

        try {
            setIsSubmitting(true);
            const response = await axiosInstance.post('/api/feed/reactions/toggle', {
                type: itemType,
                id: itemId,
                emoji
            });
            onReactionsChange?.(response.data.reactions || {});
        } catch (error) {
            console.error('Error toggling reaction:', error);
            alert(error.response?.data?.error || 'Error al registrar la reacción');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="px-5 py-3 border-t border-gray-50 mt-2">
            <div className="flex flex-wrap items-center gap-2">
                {reactionEntries.map(([emoji, users]) => {
                    const reactedByUser = users.some((reactionUser) => reactionUser.user_id === userId);
                    return (
                        <button
                            key={emoji}
                            type="button"
                            disabled={isSubmitting}
                            onClick={() => handleToggleReaction(emoji)}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors ${
                                reactedByUser
                                    ? 'border-sura-blue bg-blue-50 text-sura-blue'
                                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                            } ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
                            title={users.map((reactionUser) => reactionUser.user_name).join(', ')}
                        >
                            <span>{emoji}</span>
                            <span className="text-xs font-semibold">{users.length}</span>
                        </button>
                    );
                })}

                {FEED_REACTION_EMOJIS.filter((emoji) => !reactions[emoji]).map((emoji) => (
                    <button
                        key={emoji}
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => handleToggleReaction(emoji)}
                        className={`inline-flex items-center justify-center rounded-full border border-dashed border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-500 transition-colors hover:border-gray-300 hover:bg-white hover:text-gray-700 ${
                            isSubmitting ? 'opacity-60 cursor-not-allowed' : ''
                        }`}
                        title={`Reaccionar con ${emoji}`}
                    >
                        {emoji}
                    </button>
                ))}
            </div>
        </div>
    );
};

// Visor de diapositivas: el backend convirtio la presentacion a un PNG por lamina
const SlideStage = ({ slideUrl, index, total, onPrev, onNext, ratio, onRatio, className, imgClassName }) => (
    <>
        <img
            key={index}
            src={slideUrl(index)}
            alt={`Diapositiva ${index + 1} de ${total}`}
            className={imgClassName}
            onLoad={onRatio ? (e) => {
                const { naturalWidth, naturalHeight } = e.target;
                if (naturalWidth && naturalHeight) onRatio(naturalWidth / naturalHeight);
            } : undefined}
        />
        <button
            type="button"
            onClick={onPrev}
            disabled={index === 0}
            aria-label="Diapositiva anterior"
            className={className}
            style={{ left: '0.75rem' }}
        >
            <ChevronLeft size={22} />
        </button>
        <button
            type="button"
            onClick={onNext}
            disabled={index >= total - 1}
            aria-label="Diapositiva siguiente"
            className={className}
            style={{ right: '0.75rem' }}
        >
            <ChevronRight size={22} />
        </button>
    </>
);

const SlideDeck = ({ dir, total, fileUrl, nombre }) => {
    const [index, setIndex] = useState(0);
    const [ratio, setRatio] = useState(16 / 9);
    const [expanded, setExpanded] = useState(false);
    const touchStartX = useRef(null);

    const slideUrl = useCallback((i) => `${dir}/d${i + 1}.png`, [dir]);

    const go = useCallback((next) => {
        setIndex((prev) => {
            const target = typeof next === 'function' ? next(prev) : next;
            if (target < 0 || target > total - 1) return prev;
            return target;
        });
    }, [total]);

    const prev = useCallback(() => go((i) => i - 1), [go]);
    const next = useCallback(() => go((i) => i + 1), [go]);

    // Precargar la lamina siguiente para que el avance se sienta instantaneo
    useEffect(() => {
        if (index + 1 < total) {
            const img = new Image();
            img.src = slideUrl(index + 1);
        }
    }, [index, total, slideUrl]);

    useEffect(() => {
        if (!expanded) return undefined;
        const onKey = (e) => {
            if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); next(); }
            else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
            else if (e.key === 'Escape') setExpanded(false);
        };
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [expanded, next, prev]);

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
    };

    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        if (touchStartX.current === null) return;
        const delta = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 45) (delta < 0 ? next : prev)();
        touchStartX.current = null;
    };

    if (!dir || !total) return null;

    const arrowClass = 'absolute top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/45 text-white backdrop-blur-sm ' +
        'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 ' +
        'hover:bg-black/70 disabled:opacity-0 disabled:pointer-events-none transition-opacity';

    return (
        <div className="mt-3">
            <div
                className="relative bg-gray-900 group focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400"
                style={{ aspectRatio: ratio }}
                tabIndex={0}
                role="group"
                aria-label={`Presentacion${nombre ? `: ${nombre}` : ''}, diapositiva ${index + 1} de ${total}`}
                onKeyDown={handleKeyDown}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                <SlideStage
                    slideUrl={slideUrl}
                    index={index}
                    total={total}
                    onPrev={prev}
                    onNext={next}
                    onRatio={setRatio}
                    className={arrowClass}
                    imgClassName="w-full h-full object-contain"
                />

                <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    aria-label="Ver a pantalla completa"
                    className="absolute top-3 right-3 p-2 rounded-lg bg-black/45 text-white backdrop-blur-sm opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 hover:bg-black/70 transition-opacity"
                >
                    <Maximize2 size={16} />
                </button>

                <span className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full bg-black/55 text-white text-xs font-medium tabular-nums backdrop-blur-sm">
                    {index + 1} / {total}
                </span>
            </div>

            <div className="px-5 pt-3 flex items-center gap-3">
                <div className="flex-1 flex items-center gap-1">
                    {total <= 24 ? (
                        Array.from({ length: total }, (_, i) => (
                            <button
                                key={i}
                                type="button"
                                tabIndex={-1}
                                aria-label={`Ir a la diapositiva ${i + 1}`}
                                onClick={() => go(i)}
                                className={`h-1 flex-1 rounded-full transition-colors ${i <= index ? 'bg-blue-500' : 'bg-gray-200 hover:bg-gray-300'}`}
                            />
                        ))
                    ) : (
                        <div className="h-1 w-full rounded-full bg-gray-200 overflow-hidden">
                            <div className="h-full bg-blue-500 transition-all" style={{ width: `${((index + 1) / total) * 100}%` }} />
                        </div>
                    )}
                </div>
                {fileUrl && (
                    <a
                        href={fileUrl}
                        download
                        className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-blue-600 transition-colors flex-shrink-0"
                    >
                        <Download size={14} /> Descargar
                    </a>
                )}
            </div>

            {expanded && (
                <div className="fixed inset-0 z-[60] bg-gray-950 flex flex-col">
                    <div className="flex items-center justify-between gap-4 px-5 py-3 text-gray-300">
                        <span className="flex items-center gap-2 text-sm font-medium truncate">
                            <Presentation size={16} className="text-amber-400 flex-shrink-0" />
                            <span className="truncate">{nombre || 'Presentacion'}</span>
                        </span>
                        <div className="flex items-center gap-4 flex-shrink-0">
                            <span className="text-sm tabular-nums text-gray-400">{index + 1} / {total}</span>
                            <button type="button" onClick={() => setExpanded(false)} aria-label="Cerrar" className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                    </div>

                    <div
                        className="flex-1 relative flex items-center justify-center px-4 pb-4 group"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        <SlideStage
                            slideUrl={slideUrl}
                            index={index}
                            total={total}
                            onPrev={prev}
                            onNext={next}
                            className={arrowClass.replace('opacity-0 ', '')}
                            imgClassName="max-h-full max-w-full object-contain"
                        />
                    </div>

                    <div className="h-1 bg-white/10">
                        <div className="h-full bg-blue-500 transition-all" style={{ width: `${((index + 1) / total) * 100}%` }} />
                    </div>
                </div>
            )}
        </div>
    );
};

const PostImage = ({ src }) => {
    const [isVertical, setIsVertical] = useState(false);

    return (
        <div className={isVertical ? 'px-5 pb-2' : ''}>
            <img
                src={src}
                alt="Post content"
                className={`block w-full h-auto bg-gray-50 ${isVertical ? 'rounded-xl' : ''}`}
                onLoad={(e) => {
                    const { naturalWidth, naturalHeight } = e.target;
                    if (naturalHeight > naturalWidth * 1.05) setIsVertical(true);
                }}
                onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
            />
        </div>
    );
};

// Card de anuncio (email)
const AnuncioCard = ({ anuncio, canPin, onTogglePin, userId, onReactionsChange }) => {
    return (
        <article className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow relative">
            <div className="p-5 pb-3 flex items-start justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center">
                        <Mail size={20} />
                    </div>
                    <div>
                        <h4 className="font-semibold text-gray-900">{anuncio.subject || '(Sin asunto)'}</h4>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="px-2 py-0.5 rounded-md bg-purple-50 text-purple-600">Anuncio</span>
                            <span>•</span>
                            <span>{anuncio.from}</span>
                            <span>•</span>
                            <span>{anuncio.date}</span>
                        </div>
                    </div>
                </div>
                {canPin && (
                    <button
                        onClick={() => onTogglePin('anuncio', anuncio.id)}
                        className="p-2 text-gray-400 hover:text-amber-500 rounded-full hover:bg-amber-50 transition-colors"
                        title={anuncio.is_pinned ? 'Desfijar' : 'Fijar'}
                    >
                        {anuncio.is_pinned ? <PinOff size={18} /> : <Pin size={18} />}
                    </button>
                )}
            </div>
            <div className="px-5 py-2">
                <div className="prose prose-sm max-w-none text-gray-700 line-clamp-6">
                    {anuncio.is_html ? (
                        <div dangerouslySetInnerHTML={{ __html: anuncio.body }} />
                    ) : (
                        anuncio.body || anuncio.preview
                    )}
                </div>
            </div>
            {anuncio.has_attachments && (
                <div className="px-5 pb-2">
                    <span className="inline-flex items-center gap-1 text-xs text-gray-400"><Paperclip size={12} /> Adjuntos</span>
                </div>
            )}
            {anuncio.allow_reactions ? (
                <ReactionBar
                    itemType="anuncio"
                    itemId={anuncio.id}
                    reactions={anuncio.reactions}
                    allowReactions={anuncio.allow_reactions}
                    userId={userId}
                    onReactionsChange={onReactionsChange}
                />
            ) : (
                <div className="px-5 py-3 border-t border-gray-50 mt-2" />
            )}
        </article>
    );
};

// ---------- Item del feed (movimiento) ----------
//
// En la carga, los primeros items suben en cascada apenas se va el esqueleto
// (sin adelantarse a la entrada del resto de Inicio). Lo
// que llega despues (una publicacion nueva, los anuncios) se abre en su lugar
// y empuja suave a los demas; lo que se borra se cierra; y si cambia el orden
// (fijar o desfijar), el item viaja a su nueva posicion.
const RESORTE_FEED = { type: 'spring', stiffness: 260, damping: 32 };

const ItemFeed = React.forwardRef(({ children, indice, deLaCarga, retardo = 0 }, ref) => (
    <motion.div
        ref={ref}
        layout="position"
        initial={deLaCarga ? { opacity: 0, y: 18 } : { opacity: 0, height: 0 }}
        animate={{ opacity: 1, y: 0, height: 'auto', transitionEnd: { overflow: 'visible' } }}
        exit={{ opacity: 0, height: 0, overflow: 'hidden' }}
        transition={deLaCarga ? { ...RESORTE_FEED, delay: retardo + Math.min(indice * 0.07, 0.35) } : RESORTE_FEED}
        style={{ overflow: deLaCarga ? 'visible' : 'hidden' }}
    >
        <div className="pb-6">{children}</div>
    </motion.div>
));

const FeedWidget = ({ refreshTrigger, canCreate, anuncios = [] }) => {
    const { user } = useAuth();
    const [posts, setPosts] = useState([]);
    const [localAnuncios, setLocalAnuncios] = useState(anuncios);
    const [loading, setLoading] = useState(true);

    // Hasta cuando lo que aparece cuenta como "de la carga" (cascada) y no como
    // algo nuevo que se abre en su lugar.
    const cargaHasta = useRef(null);
    const montadoEn = useRef(Date.now());
    useEffect(() => {
        if (!loading && cargaHasta.current == null) cargaHasta.current = Date.now() + 1500;
    }, [loading]);
    const enCarga = cargaHasta.current == null || Date.now() < cargaHasta.current;
    // La cascada no arranca antes de que Inicio termine de entrar (~0.45 s).
    const retardoCarga = Math.max(0, 0.45 - (Date.now() - montadoEn.current) / 1000);
    const envolver = (clave, indice, contenido) => (
        <ItemFeed key={clave} indice={indice} deLaCarga={enCarga} retardo={retardoCarga}>
            {contenido}
        </ItemFeed>
    );

    const isAdminOrGh = user?.role === 'admin';
    const canPin = user?.username === 'dfalfan';

    const fetchPosts = async () => {
        try {
            const response = await axiosInstance.get('/api/publicaciones');
            if (response.data.publicaciones) {
                setPosts(response.data.publicaciones);
            }
        } catch (error) {
            console.error('Error fetching posts:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) fetchPosts();
    }, [refreshTrigger, user]);

    useEffect(() => {
        setLocalAnuncios(anuncios);
    }, [anuncios]);

    const handleDelete = async (postId) => {
        if (!window.confirm('¿Estás seguro de eliminar esta publicación?')) return;
        try {
            await axiosInstance.delete(`/api/publicaciones/${postId}`);
            setPosts(posts.filter(p => p.id !== postId));
        } catch (error) {
            console.error('Error deleting post:', error);
            alert('Error al eliminar la publicación');
        }
    };

    const handleTogglePin = async (type, id) => {
        try {
            const response = await axiosInstance.post('/api/feed/pin', { type, id });
            if (response.data.success) {
                if (type === 'publicacion') {
                    fetchPosts();
                }
                // Anuncios se refrescan via el parent (HomeView)
                if (type === 'anuncio' && window.__refreshAnuncios) {
                    window.__refreshAnuncios();
                }
            }
        } catch (error) {
            console.error('Error toggling pin:', error);
        }
    };

    const updateItemReactions = (itemType, itemId, nextReactions) => {
        if (itemType === 'publicacion') {
            setPosts((currentPosts) =>
                currentPosts.map((post) =>
                    post.id === itemId ? { ...post, reactions: nextReactions } : post
                )
            );
            return;
        }

        setLocalAnuncios((currentAnuncios) =>
            currentAnuncios.map((anuncio) =>
                anuncio.id === itemId ? { ...anuncio, reactions: nextReactions } : anuncio
            )
        );
    };

    // Voto para encuestas legado (sin preguntas)
    const handleLegacyVote = async (postId, optionId) => {
        try {
            await axiosInstance.post(`/api/publicaciones/${postId}/voto`, {
                opcion_id: optionId
            });
            fetchPosts();
        } catch (error) {
            alert(error.response?.data?.error || 'Error al registrar voto');
        }
    };

    // Mezclar publicaciones y anuncios cronológicamente
    const mergedFeed = React.useMemo(() => {
        const pubItems = posts.map(p => ({
            ...p,
            _feedType: 'publicacion',
            _sortDate: p.created_at,
            _isPinned: p.is_pinned || false,
            _pinnedAt: p.pinned_at
        }));

        const anuncioItems = localAnuncios.map(a => ({
            ...a,
            _feedType: 'anuncio',
            _sortDate: a.date_iso || a.date,
            _isPinned: a.is_pinned || false,
            _pinnedAt: a.pinned_at
        }));

        const all = [...pubItems, ...anuncioItems];

        // Pinned primero (por pinned_at desc), luego cronológico (más reciente primero)
        all.sort((a, b) => {
            if (a._isPinned && !b._isPinned) return -1;
            if (!a._isPinned && b._isPinned) return 1;
            if (a._isPinned && b._isPinned) {
                return new Date(b._pinnedAt) - new Date(a._pinnedAt);
            }
            return new Date(b._sortDate) - new Date(a._sortDate);
        });

        return all;
    }, [posts, localAnuncios]);

    // El esqueleto aparece con la entrada de Inicio y, al llegar el feed, se
    // funde antes de que suban las publicaciones: sin cortes en seco.
    const mostrarEsqueleto = loading && posts.length === 0;

    return (
        <AnimatePresence mode="wait">
        {mostrarEsqueleto ? (
            <motion.div
                key="esqueleto"
                className="space-y-6 mb-8"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0, transition: { ...RESORTE_FEED, delay: 0.4 } }}
                exit={{ opacity: 0, transition: { duration: 0.18 } }}
            >
                {[1, 2].map(i => (
                    <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm skeleton">
                        <div className="flex gap-4 mb-4">
                            <div className="w-10 h-10 bg-slate-200 rounded-full"></div>
                            <div className="flex-1 space-y-2">
                                <div className="h-4 bg-slate-200/70 rounded w-1/4"></div>
                                <div className="h-3 bg-slate-200/70 rounded w-1/6"></div>
                            </div>
                        </div>
                        <div className="h-20 bg-slate-200/60 rounded-xl mb-4"></div>
                    </div>
                ))}
            </motion.div>
        ) : (
        <motion.div key="feed" className="mb-8">
            <AnimatePresence>
            {mergedFeed.map((item, indice) => {
                // Render anuncio
                if (item._feedType === 'anuncio') {
                    return envolver(`anuncio-${item.id}`, indice, (
                        <AnuncioCard
                            key={`anuncio-${item.id}`}
                            anuncio={item}
                            canPin={canPin}
                            onTogglePin={handleTogglePin}
                            userId={user?.id}
                            onReactionsChange={(nextReactions) => updateItemReactions('anuncio', item.id, nextReactions)}
                        />
                    ));
                }

                // Render publicación
                const post = item;
                const isCreator = post.created_by === user?.id;
                const pollResultsArePublic = post.resultados_publicos === true;
                const canSeePollResults = post.can_see_results === true;
                const isPrivatePoll = post.type === 'encuesta' && !pollResultsArePublic;

                return envolver(`pub-${post.id}`, indice, (
                    <article key={`pub-${post.id}`} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">

                        {/* Header */}
                        <div className="p-5 pb-3 flex items-start justify-between">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span className="capitalize px-2 py-0.5 rounded-md bg-gray-100">
                                        {post.type === 'encuesta' ? 'Encuesta' : post.type}
                                    </span>
                                    {post.type === 'encuesta' && post.is_anonima && (
                                        <span className="flex items-center gap-1 text-gray-400 px-1.5 py-0.5 bg-gray-100 rounded-md">
                                            <EyeOff size={10} /> Anónima
                                        </span>
                                    )}
                                    {isPrivatePoll && (
                                        <span className="flex items-center gap-1 text-gray-400 px-1.5 py-0.5 bg-gray-100 rounded-md">
                                            <Lock size={10} /> Privada
                                        </span>
                                    )}
                                    <span>•</span>
                                    <span>{formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: es })}</span>
                                    {post.type === 'encuesta' && post.is_expired && (
                                        <span className="text-red-500 font-medium">Finalizada</span>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center gap-1">
                                {canPin && (
                                    <button
                                        onClick={() => handleTogglePin('publicacion', post.id)}
                                        className="p-2 text-gray-400 hover:text-amber-500 rounded-full hover:bg-amber-50 transition-colors"
                                        title={post.is_pinned ? 'Desfijar' : 'Fijar'}
                                    >
                                        {post.is_pinned ? <PinOff size={18} /> : <Pin size={18} />}
                                    </button>
                                )}
                                {isAdminOrGh && (
                                    <div className="relative group">
                                        <button className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
                                            <MoreHorizontal size={20} />
                                        </button>
                                        <div className="absolute right-0 top-full mt-1 w-32 bg-white rounded-xl shadow-lg border border-gray-100 py-1 hidden group-hover:block z-10">
                                            <button
                                                onClick={() => handleDelete(post.id)}
                                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                            >
                                                <Trash2 size={14} /> Eliminar
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Title */}
                        {post.titulo && (
                            <div className="px-5 pt-1 pb-1">
                                <h3 className="text-lg font-bold text-gray-900 leading-snug"><TituloConMarcas titulo={post.titulo} /></h3>
                            </div>
                        )}

                        {/* Content */}
                        <div className="px-5 py-2">
                            <div className="prose prose-sm max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: post.content }} />
                        </div>

                        {/* Poll — multi-pregunta (nuevo formato) */}
                        {post.type === 'encuesta' && post.preguntas?.length > 0 && (
                            <div className="mx-5 mb-5 mt-3 space-y-4">
                                {/* Banner si no ha votado todo */}
                                {!post.all_questions_answered && !post.is_expired && (
                                    <div className="flex items-center justify-between px-3 py-2 bg-blue-50 rounded-xl border border-blue-100">
                                        <p className="text-sura-blue font-semibold text-sm">Tu opinión cuenta, ¡vota ahora!</p>
                                        <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded-md">
                                            {post.questions_answered}/{post.total_questions} respondidas
                                        </span>
                                    </div>
                                )}

                                {post.preguntas.map((pregunta, idx) => (
                                    <div key={pregunta.id} className="bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-100 p-4 shadow-sm">
                                        {post.total_questions > 1 && (
                                            <p className="text-xs font-bold text-blue-500 mb-2">Pregunta {idx + 1}</p>
                                        )}
                                        <p className="text-sm font-semibold text-gray-800 mb-3">{pregunta.texto}</p>
                                        <PreguntaPoll
                                            pregunta={pregunta}
                                            postId={post.id}
                                            postIsExpired={post.is_expired}
                                            postIsAnonima={post.is_anonima}
                                            canSeeResults={canSeePollResults}
                                            isAdminOrCreator={isCreator}
                                            onVoted={fetchPosts}
                                        />
                                        {canSeePollResults && (
                                            <div className="text-xs text-gray-400 mt-3 text-center border-t border-blue-100/50 pt-2">
                                                {pregunta.total_votes} {pregunta.tipo === 'texto' ? (pregunta.total_votes === 1 ? 'respuesta' : 'respuestas') : (pregunta.total_votes === 1 ? 'voto' : 'votos')}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Poll legado (formato antiguo sin preguntas) */}
                        {post.type === 'encuesta' && (!post.preguntas || post.preguntas.length === 0) && post.options?.length > 0 && (
                            <div className="mx-5 mb-5 mt-2 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-100 p-4 shadow-sm">
                                {!post.user_has_voted && !post.is_expired && (
                                    <div className="mb-4 flex items-center justify-between">
                                        <p className="text-sura-blue font-semibold text-sm">Tu opinión cuenta, ¡vota ahora!</p>
                                        <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded-md">Encuesta abierta</span>
                                    </div>
                                )}
                                <div className="space-y-3">
                                    {(() => {
                                        const hasVotedOrExpired = post.user_has_voted || post.is_expired;
                                        const canSeeResultsLegacy = canSeePollResults;
                                        const showResults = hasVotedOrExpired && canSeeResultsLegacy;
                                        const showVotedButHidden = hasVotedOrExpired && !canSeeResultsLegacy;

                                        return post.options.map((option) => {
                                            const isSelected = post.user_voted_option_id === option.id;
                                            return (
                                                <div key={option.id}>
                                                    {showResults ? (
                                                        <div className="relative h-11 w-full bg-white rounded-lg overflow-hidden border border-gray-100 shadow-sm">
                                                            <div className={`absolute top-0 left-0 h-full transition-all duration-700 ease-out ${isSelected ? 'bg-sura-blue/10' : 'bg-gray-100/50'}`} style={{ width: `${option.percentage}%` }} />
                                                            <div className="absolute inset-0 flex items-center justify-between px-4 z-10">
                                                                <div className="flex items-center gap-2">
                                                                    {isSelected && (
                                                                        <div className="w-5 h-5 rounded-full bg-sura-blue text-white flex items-center justify-center shadow-sm">
                                                                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                                                        </div>
                                                                    )}
                                                                    <span className={`text-sm font-medium ${isSelected ? 'text-sura-blue' : 'text-gray-700'}`}>{option.texto}</span>
                                                                </div>
                                                                <span className="text-sm text-gray-700 font-bold">{Math.round(option.percentage)}%</span>
                                                            </div>
                                                        </div>
                                                    ) : showVotedButHidden ? (
                                                        <div className={`relative h-11 w-full rounded-lg overflow-hidden border shadow-sm flex items-center px-4 gap-2 ${isSelected ? 'bg-sura-blue/5 border-sura-blue/20' : 'bg-gray-50 border-gray-100'}`}>
                                                            {isSelected && (
                                                                <div className="w-5 h-5 rounded-full bg-sura-blue text-white flex items-center justify-center shadow-sm flex-shrink-0">
                                                                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                                                </div>
                                                            )}
                                                            <span className={`text-sm font-medium ${isSelected ? 'text-sura-blue' : 'text-gray-500'}`}>{option.texto}</span>
                                                        </div>
                                                    ) : (
                                                        <button onClick={() => handleLegacyVote(post.id, option.id)} className="w-full text-left px-4 py-3 bg-white border border-blue-100/50 rounded-xl shadow-sm hover:shadow-md hover:border-sura-blue hover:-translate-y-0.5 transition-all duration-200 group flex items-center gap-3">
                                                            <div className="w-5 h-5 rounded-full border-2 border-gray-300 group-hover:border-sura-blue flex items-center justify-center transition-colors">
                                                                <div className="w-2.5 h-2.5 rounded-full bg-sura-blue opacity-0 group-hover:opacity-100 transition-opacity transform scale-0 group-hover:scale-100" />
                                                            </div>
                                                            <span className="text-sm font-medium text-gray-700 group-hover:text-sura-blue transition-colors">{option.texto}</span>
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        });
                                    })()}
                                    {(() => {
                                        const hasVotedOrExpired = post.user_has_voted || post.is_expired;
                                        const canSeeResultsLegacy = canSeePollResults;
                                        if (hasVotedOrExpired && !canSeeResultsLegacy) return (
                                            <div className="flex items-center justify-center gap-1.5 text-xs text-gray-400 mt-2">
                                                <Lock size={11} />
                                                <span>Resultados visibles solo para el creador</span>
                                            </div>
                                        );
                                        return null;
                                    })()}
                                </div>
                                {canSeePollResults && (
                                    <div className="text-xs text-gray-500 mt-4 text-center font-medium border-t border-blue-100/50 pt-2">
                                        {post.total_votes} {post.total_votes === 1 ? 'voto registrado' : 'votos registrados'}
                                    </div>
                                )}
                            </div>
                        )}

                        {post.slides_dir && post.slides_count > 0 && (
                            <SlideDeck
                                dir={post.slides_dir}
                                total={post.slides_count}
                                fileUrl={post.slides_file_url}
                                nombre={post.slides_titulo}
                            />
                        )}

                        {/* Media */}
                        {(post.image_url || post.video_url) && (
                            <div className="mt-3">
                                {post.video_url && (
                                    <div className="bg-black">
                                        <video src={post.video_url} controls muted autoPlay loop playsInline preload="auto" className="w-full" controlsList="nodownload">
                                            Tu navegador no soporta videos.
                                        </video>
                                    </div>
                                )}
                                {post.image_url && (
                                    <PostImage src={post.image_url} />
                                )}
                            </div>
                        )}

                        {post.allow_reactions ? (
                            <ReactionBar
                                itemType="publicacion"
                                itemId={post.id}
                                reactions={post.reactions}
                                allowReactions={post.allow_reactions}
                                userId={user?.id}
                                onReactionsChange={(nextReactions) => updateItemReactions('publicacion', post.id, nextReactions)}
                            />
                        ) : (
                            <div className="px-5 py-3 border-t border-gray-50 mt-2" />
                        )}
                    </article>
                ));
            })}
            </AnimatePresence>
        </motion.div>
        )}
        </AnimatePresence>
    );
};

export default FeedWidget;
